import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useGameStore } from '../store/gameStore';
import { averageMetric } from '../utils/resultMetrics';
import { gameCategories } from '../data/games';
import {
  ArrowLeft, Sparkles, Play, Clock, Target, Zap,
  RefreshCw, ChevronRight, Star, Brain, Eye
} from 'lucide-react';
import { playClick } from '../utils/sounds';

interface TrainingPlan {
  id: string;
  name: string;
  description: string;
  games: { id: string; name: string; icon: string; category: string; reason: string }[];
  duration: number; // minutes
  focus: string;
  priority: 'high' | 'medium' | 'low';
}

export default function TrainingPlanPage() {
  const navigate = useNavigate();
  const { levelResults, consecutiveDays } = useGameStore();
  const [selectedPlan, setSelectedPlan] = useState<TrainingPlan | null>(null);

  // All available games flattened
  const allGames = useMemo(() => {
    return gameCategories.flatMap(cat => cat.games.map(g => ({ ...g, categoryName: cat.name })));
  }, []);

  // Analysis: which categories the user has trained recently
  const now = new Date();
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const getCompletedAt = (value: unknown) => (typeof value === 'string' ? value : '');
  const weekResults = levelResults.filter((r) => {
    const completedAt = getCompletedAt(r?.completedAt);
    return completedAt ? new Date(completedAt) > weekAgo : false;
  });
  const totalSessions = weekResults.length;

  const categoryTrainCount = useMemo(() => {
    const counts: Record<string, number> = {};
    weekResults.forEach(r => {
      const game = allGames.find(g => g.id === r.levelId);
      if (game) {
        counts[game.categoryId] = (counts[game.categoryId] || 0) + 1;
      }
    });
    return counts;
  }, [weekResults, allGames]);

  // Average accuracy and reaction
  const avgAccuracy = averageMetric(weekResults, 'accuracy');
  const avgReaction = averageMetric(weekResults, 'reactionTime');

  // Weak categories (less trained)
  const allCategoryIds = ['binocular', 'myopia', 'amblyopia', 'memory', 'fatigue', 'spatial'];
  const weakCategories = allCategoryIds.filter(c => (categoryTrainCount[c] || 0) < 2);

  // Generate smart plans
  const plans = useMemo((): TrainingPlan[] => {
    const result: TrainingPlan[] = [];

    // Plan 1: Balanced daily plan
    const balancedGames = allCategoryIds.slice(0, 4).map(catId => {
      const catGames = allGames.filter(g => g.categoryId === catId && g.difficulty <= 2);
      const unplayed = catGames.filter(g => !weekResults.find(r => r.levelId === g.id));
      const pick = unplayed.length > 0 ? unplayed[Math.floor(Math.random() * unplayed.length)] : catGames[0];
      return pick;
    }).filter(Boolean);

    if (balancedGames.length >= 3) {
      result.push({
        id: 'balanced',
        name: '每日均衡训练',
        description: '涵盖多种视觉功能的综合训练方案',
        games: balancedGames.map(g => ({
          id: g.id, name: g.name, icon: g.icon,
          category: g.categoryName,
          reason: '均衡训练各项视觉能力',
        })),
        duration: 15,
        focus: '综合视觉能力',
        priority: 'medium',
      });
    }

    // Plan 2: Weakness-focused plan
    if (weakCategories.length > 0) {
      const weakGames = weakCategories.slice(0, 3).map(catId => {
        const catGames = allGames.filter(g => g.categoryId === catId && g.difficulty <= 2);
        return catGames[Math.floor(Math.random() * catGames.length)];
      }).filter(Boolean);

      if (weakGames.length >= 2) {
        const categoryNames: Record<string, string> = {
          binocular: '双眼视', myopia: '近视防控', amblyopia: '斜弱视',
          memory: '脑记忆', fatigue: '视疲劳缓解', spatial: '空间知觉'
        };
        result.push({
          id: 'weakness',
          name: '薄弱项强化',
          description: `本周${weakCategories.map(c => categoryNames[c]).join('、')}训练不足，需要加强`,
          games: weakGames.map(g => ({
            id: g.id, name: g.name, icon: g.icon,
            category: g.categoryName,
            reason: '本周训练次数不足，需要补强',
          })),
          duration: 12,
          focus: '薄弱项补强',
          priority: 'high',
        });
      }
    }

    // Plan 3: Myopia and fatigue focus (always available)
    const myopiaGames = allGames.filter(g => g.categoryId === 'myopia' || g.categoryId === 'fatigue');
    const myopiaPicks = myopiaGames.sort(() => Math.random() - 0.5).slice(0, 4);
    if (myopiaPicks.length >= 3) {
      result.push({
        id: 'myopia-intensive',
        name: '近视防控加强',
        description: '聚焦调节与放松训练，帮助稳定训练节奏',
        games: myopiaPicks.map(g => ({
          id: g.id, name: g.name, icon: g.icon,
          category: g.categoryName,
          reason: '提升调节灵活度与持续专注表现',
        })),
        duration: 18,
        focus: '调节与放松',
        priority: totalSessions >= 6 ? 'medium' : 'high',
      });
    }

    // Plan 4: Eye fatigue recovery
    if (avgReaction > 900 || (weekResults.some(r => r.accuracyMeasured !== false) && avgAccuracy < 60)) {
      const fatigueGames = allGames.filter(g => g.categoryId === 'fatigue');
      const picks = fatigueGames.sort(() => Math.random() - 0.5).slice(0, 3);
      result.push({
        id: 'fatigue-recovery',
        name: '视疲劳恢复',
        description: avgReaction > 900 ? '反应速度偏慢，优先放松恢复' : '准确率偏低，适当放松后再训练',
        games: picks.map(g => ({
          id: g.id, name: g.name, icon: g.icon,
          category: g.categoryName,
          reason: '放松眼肌，恢复视觉活力',
        })),
        duration: 10,
        focus: '放松恢复',
        priority: 'medium',
      });
    }

    // Plan 5: Challenge mode for advanced users
    if (avgAccuracy > 85 && consecutiveDays >= 5) {
      const hardGames = allGames.filter(g => g.difficulty === 3);
      const picks = hardGames.sort(() => Math.random() - 0.5).slice(0, 4);
      result.push({
        id: 'challenge',
        name: '进阶挑战',
        description: '表现优秀！尝试更高难度的训练',
        games: picks.map(g => ({
          id: g.id, name: g.name, icon: g.icon,
          category: g.categoryName,
          reason: '挑战极限，突破视觉潜力',
        })),
        duration: 18,
        focus: '突破提升',
        priority: 'low',
      });
    }

    // Plan 6: Binocular focus (always available)
    const binocularGames = allGames.filter(g => g.categoryId === 'binocular').sort(() => Math.random() - 0.5).slice(0, 3);
    result.push({
      id: 'binocular-focus',
      name: '双眼视专项',
      description: '强化双眼协调与融合功能',
      games: binocularGames.map(g => ({
        id: g.id, name: g.name, icon: g.icon,
        category: g.categoryName,
        reason: '双眼协调是视力健康的基础',
      })),
      duration: 12,
      focus: '双眼协调',
      priority: 'medium',
    });

    return result;
  }, [allGames, weekResults, weakCategories, avgAccuracy, avgReaction, consecutiveDays, totalSessions]);

  const getPriorityConfig = (priority: 'high' | 'medium' | 'low') => {
    switch (priority) {
      case 'high': return { label: '优先推荐', color: 'text-red-500', bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200 dark:border-red-800' };
      case 'medium': return { label: '推荐', color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-900/20', border: 'border-emerald-200 dark:border-emerald-800' };
      case 'low': return { label: '可选', color: 'text-gray-500', bg: 'bg-gray-50 dark:bg-gray-700', border: 'border-gray-200 dark:border-gray-600' };
    }
  };

  const containerAnim = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.06 } } };
  const cardAnim = { hidden: { y: 20, opacity: 0 }, show: { y: 0, opacity: 1, transition: { duration: 0.4 } } };

  // Plan detail view
  if (selectedPlan) {
    return (
      <div className="min-h-screen w-full bg-[var(--bg)]">
        <header className="sticky top-0 z-30 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between max-w-2xl mx-auto px-4 py-3">
            <button onClick={() => setSelectedPlan(null)} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-500 dark:text-gray-300">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-base font-bold text-gray-800 dark:text-gray-100">{selectedPlan.name}</h1>
            <div className="w-9" />
          </div>
        </header>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto px-4 py-6 pb-28 space-y-4"
        >
          {/* Plan info */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100 dark:from-emerald-900/30 dark:to-teal-900/30 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">{selectedPlan.focus}</h3>
                <p className="text-xs text-gray-400">{selectedPlan.description}</p>
              </div>
            </div>
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <Clock className="w-3.5 h-3.5" /> 约{selectedPlan.duration}分钟
              </span>
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <Target className="w-3.5 h-3.5" /> {selectedPlan.games.length}个项目
              </span>
            </div>
          </div>

          {/* Game list */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-4">训练项目</h3>
            <div className="space-y-3">
              {selectedPlan.games.map((game, idx) => (
                <motion.div
                  key={game.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.08 }}
                  className="flex items-center gap-3 bg-gray-50 dark:bg-gray-700 rounded-xl p-3.5"
                >
                  <div className="w-10 h-10 rounded-xl bg-white dark:bg-gray-600 flex items-center justify-center text-lg shadow-sm flex-shrink-0">
                    {game.icon.slice(0, 2)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-700 dark:text-gray-200">{game.name}</p>
                    <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">{game.reason}</p>
                  </div>
                  <button
                    onClick={() => { playClick(); navigate(`/game/${game.id}`); }}
                    className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0 hover:bg-emerald-100 transition-colors"
                  >
                    <Play className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                  </button>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Tips */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3">💡 训练贴士</h3>
            <div className="space-y-2 text-xs text-gray-500 dark:text-gray-400">
              <p>• 按顺序完成训练项目效果更佳</p>
              <p>• 每个项目之间休息30秒，闭眼放松</p>
              <p>• 训练完毕后远眺窗外2-3分钟</p>
              <p>• 如感到眼睛疲劳请立即停止休息</p>
            </div>
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <nav className="fixed bottom-0 left-0 right-0 mx-auto sm:max-w-md md:max-w-lg sm:border-x z-40 bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg border-t border-gray-100 dark:border-gray-700">
          <div className="max-w-2xl mx-auto flex items-center justify-center py-3 px-4">
            <button
              onClick={() => { playClick(); navigate(`/game/${selectedPlan.games[0]?.id}`); }}
              className="w-full max-w-xs py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-teal-500 shadow-md shadow-emerald-200 dark:shadow-none hover:shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2"
            >
              <Play className="w-4 h-4" />
              开始训练
            </button>
          </div>
        </nav>
      </div>
    );
  }

  // Plan list view
  return (
    <div className="min-h-screen w-full bg-[var(--bg)]">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between max-w-2xl mx-auto px-4 py-3">
          <button onClick={() => navigate('/parent')} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-500 dark:text-gray-300">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-base font-bold text-gray-800 dark:text-gray-100">智能训练计划</h1>
          <div className="w-9" />
        </div>
      </header>

      <motion.div
        variants={containerAnim}
        initial="hidden"
        animate="show"
        className="max-w-2xl mx-auto px-4 py-6 pb-28 space-y-4"
      >
        {/* Smart Analysis Summary */}
        <motion.div variants={cardAnim} className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-5 shadow-lg text-white">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-emerald-100" />
            <h3 className="text-sm font-semibold">智能分析结果</h3>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="bg-white/15 rounded-xl p-2.5 text-center backdrop-blur-sm">
              <p className="text-lg font-bold">{Math.round(avgAccuracy)}%</p>
              <p className="text-[10px] text-white/70">本周准确率</p>
            </div>
            <div className="bg-white/15 rounded-xl p-2.5 text-center backdrop-blur-sm">
              <p className="text-lg font-bold">{weekResults.length}</p>
              <p className="text-[10px] text-white/70">本周训练次</p>
            </div>
            <div className="bg-white/15 rounded-xl p-2.5 text-center backdrop-blur-sm">
              <p className="text-lg font-bold">{weakCategories.length}</p>
              <p className="text-[10px] text-white/70">薄弱项目</p>
            </div>
          </div>
          <p className="text-xs text-white/80 leading-relaxed">
            {weekResults.length === 0
              ? '本周还未开始训练，建议从均衡训练计划开始 🌟'
              : weakCategories.length > 2
                ? `有${weakCategories.length}个分类训练不足，建议优先补强薄弱项 📋`
                : avgAccuracy > 85
                  ? '训练表现优秀！可以尝试进阶挑战 🏆'
                  : '训练进展良好，继续保持训练节奏 💪'}
          </p>
        </motion.div>

        {/* Plans List */}
        <motion.div variants={cardAnim} className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">推荐方案</h3>
            <span className="text-[10px] text-gray-400">根据训练数据智能生成</span>
          </div>

          {plans.sort((a, b) => {
            const order = { high: 0, medium: 1, low: 2 };
            return order[a.priority] - order[b.priority];
          }).map((plan, idx) => {
            const config = getPriorityConfig(plan.priority);
            return (
              <motion.button
                key={plan.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + idx * 0.08 }}
                onClick={() => { setSelectedPlan(plan); playClick(); }}
                className="w-full text-left bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-all active:scale-[0.98]"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 flex items-center justify-center flex-shrink-0">
                      {plan.id === 'balanced' && <Target className="w-5 h-5 text-emerald-500" />}
                      {plan.id === 'weakness' && <Zap className="w-5 h-5 text-amber-500" />}
                      {plan.id === 'myopia-intensive' && <Eye className="w-5 h-5 text-red-500" />}
                      {plan.id === 'fatigue-recovery' && <RefreshCw className="w-5 h-5 text-blue-500" />}
                      {plan.id === 'challenge' && <Star className="w-5 h-5 text-violet-500" />}
                      {plan.id === 'binocular-focus' && <Brain className="w-5 h-5 text-indigo-500" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100">{plan.name}</h4>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${config.color} ${config.bg} ${config.border} border`}>
                          {config.label}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed line-clamp-2">
                        {plan.description}
                      </p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-[10px] text-gray-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" />{plan.duration}分钟
                        </span>
                        <span className="text-[10px] text-gray-400 flex items-center gap-1">
                          <Target className="w-3 h-3" />{plan.games.length}项
                        </span>
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600 mt-1 flex-shrink-0" />
                </div>
              </motion.button>
            );
          })}
        </motion.div>

        {/* How it works */}
        <motion.div variants={cardAnim} className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3">🧠 智能推荐原理</h3>
          <div className="space-y-2.5 text-xs text-gray-500 dark:text-gray-400">
            <div className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0 text-[10px] font-bold text-emerald-600">1</span>
              <p>分析本周训练数据，识别薄弱视觉功能</p>
            </div>
            <div className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0 text-[10px] font-bold text-emerald-600">2</span>
              <p>统计最近训练频次与完成稳定性</p>
            </div>
            <div className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0 text-[10px] font-bold text-emerald-600">3</span>
              <p>结合当前表现（准确率、反应速度）动态分层</p>            </div>
            <div className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0 text-[10px] font-bold text-emerald-600">4</span>
              <p>自动生成个性化、优先级排序的训练方案</p>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 mx-auto sm:max-w-md md:max-w-lg sm:border-x z-40 bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg border-t border-gray-100 dark:border-gray-700">
        <div className="max-w-2xl mx-auto flex items-center justify-center py-3 px-4">
          <button
            onClick={() => navigate('/parent')}
            className="px-8 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-teal-500 shadow-md shadow-emerald-200 dark:shadow-none hover:shadow-lg transition-all active:scale-95"
          >
            返回面板
          </button>
        </div>
      </nav>
    </div>
  );
}
